import { useParams, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import "./SmartLinkPage.css";
import yandexIcon from "./icons/yandex.svg";
import vkIcon from "./icons/vk.svg";
import zvukIcon from "./icons/zvuk.svg";
import mtsIcon from "./icons/kion.svg";
import appleIcon from "./icons/apple.svg";
import spotifyIcon from "./icons/spotify.svg";
import youtubeIcon from "./icons/youtube.svg";
import beatportIcon from "./icons/beatport.svg";
import junoIcon from "./icons/juno.svg";
import tiktokIcon from "./icons/tiktok.svg";
import okIcon from "./icons/ok.svg";
import deezerIcon from "./icons/deezer.svg";
import tidalIcon from "./icons/tidal.svg";
import amazonIcon from "./icons/amazon.svg";
import metrikaLogo from "./metrikaLogo.svg";
import paradigmLogo from "./paradigmLogo.png";

const RedirectIfPathEmpty = () => {
  const location = useLocation();

  useEffect(() => {
    const redirects = {
      "prdgm.online": "https://paradigm-music.ru",
      "mtrk.live": "https://metrikamusic.com",
      "mtrk.click": "https://metrikamusic.com",
    };

    const hostname = window.location.hostname.replace(/^www\./, '');
    const pathname = location.pathname;

    if (pathname === "/" && redirects[hostname]) {
      window.location.replace(redirects[hostname]);
    }
  }, [location]);

  return null;
};

// Счётчики просмотров и кликов — роуты lk-api (раньше клик уходил в workflow Bubble).
// sendBeacon переживает уход со страницы: ссылка площадки открывается сразу.
function track(slug, kind) {
  const url = `/api/smartlinks/${encodeURIComponent(slug)}/${kind}`;
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url)) return;
  } catch (err) { /* падаем на fetch */ }
  fetch(url, { method: "POST", keepalive: true }).catch(() => {});
}

export default function SmartLinkPage() {
  const { slug } = useParams();
  const [smartlinkData, setSmartlinkData] = useState(null);
  const [loading, setLoading] = useState(true);

  const currentHost = window.location.host;
  const isMetrika = currentHost.includes("mtrk.live") || currentHost.includes("mtrk.click");

  const logoSrc = isMetrika ? metrikaLogo : paradigmLogo;

  const brandName = isMetrika ? "METRIKA MUSIC" : "PARADIGM";

  const [language, setLanguage] = useState('en');

  useEffect(() => {
    const savedLang = localStorage.getItem('language');

    if (savedLang) {
      setLanguage(savedLang);
    } else {
      const hostname = window.location.hostname.replace(/^www\./, '');
      const browserLang = navigator.language || navigator.userLanguage;
      let defaultLang = 'en';

      if (hostname === 'prdgm.online') {
        defaultLang = 'ru';
      } else if (hostname === 'mtrk.live' || hostname === 'mtrk.click') {
        defaultLang = 'en';
      }

      if (browserLang.startsWith('ru')) {
        defaultLang = 'ru';
      } else if (browserLang.startsWith('en')) {
        defaultLang = 'en';
      }

      setLanguage(defaultLang);
    }
  }, []);

  const toggleLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  useEffect(() => {
    async function fetchSmartLink() {
      try {
            const response = await fetch(`/api/smartlinks/${slug}`);
            const result = await response.json();
            const data = result?.data;

        if (data && Object.keys(data).length > 0) {
          setSmartlinkData(data);
          track(slug, "view");
          document.title = `${data.artists || "Исполнитель"} - ${data.title || "Релиз"} | ${brandName}`;

          const favicon = document.querySelector("link[rel='icon']") || document.createElement("link");
          favicon.rel = "icon";
          favicon.type = "image/png";
          favicon.href = isMetrika ? "/metrika-favicon.ico" : "/paradigm-favicon.ico";
          document.head.appendChild(favicon);
        } else {
          setSmartlinkData(null);
        }
      } catch (err) {
        console.error("Ошибка при получении данных:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchSmartLink();
  }, [slug, brandName, isMetrika]);

  const handleLinkClick = () => track(slug, "click");

  if (loading)
    return (
      <div className="loader-wrapper">
        <div className={isMetrika ? "metrika-logo-loader" : "logo-loader"}>
          <img src={logoSrc} alt="Logo" />
        </div>
        <div className={isMetrika ? "spinner-metrika" : "spinner"}></div>
        <p className="loading-text">{language === 'ru' ? "Загрузка..." : "Loading..."}</p>
      </div>
    );

  if (!smartlinkData)
    return (
      <div className="not-found">
        <div className={isMetrika ? "metrika-logo-loader" : "logo-loader"}>
          <img src={logoSrc} alt="Logo" />
        </div>
        <p>{language === 'ru' ? "Упс... смарт-линк не найден :(" : "Oops... smartlink not found :("}</p>
      </div>
    );

  const {
    artists,
    title,
    coverArt: cover,
    appleMusic_url,
    yandex_url,
    beatport_url,
    juno_url,
    tiktok_url,
    ok_url,
    mts_url,
    spotify_url,
    vk_url,
    youtubeMusic_url,
    zvuk_url,
    deezer_url,
    tidal_url,
    amazonMusic_url,
  } = smartlinkData;

  // Смартлинк заводится при отгрузке, ссылки площадок появляются в день выхода (21.10.2026) —
  // до этого вместо пустой рамки надпись.
  const hasLinks = language === 'ru'
    ? [yandex_url, vk_url, zvuk_url, mts_url, appleMusic_url, spotify_url, youtubeMusic_url, ok_url, tiktok_url, beatport_url, juno_url].some(Boolean)
    : [appleMusic_url, spotify_url, youtubeMusic_url, deezer_url, tidal_url, amazonMusic_url, tiktok_url, beatport_url, juno_url].some(Boolean);

  return (
    <>
      <RedirectIfPathEmpty />

      <div className={`sl-page${isMetrika ? " sl-page--metrika" : ""}`}>
        <div className="sl-bg" aria-hidden="true">
          {cover && <div className="sl-bg-cover" style={{ backgroundImage: `url(${cover})` }} />}
        </div>

        <div className="sl-col">
          <div className="sl-top">
            <img className="sl-logo" src={logoSrc} alt={brandName} />
          </div>

          <div className="sl-card sl-hero">
            {cover && <img src={cover} alt="Cover" className="sl-cover" />}
            <div className="sl-hero-text">
              <h1 className="sl-artist">{artists || (language === 'ru' ? "Исполнитель" : "Artist")}</h1>
              <h2>{title || (language === 'ru' ? "Название трека" : "Track title")}</h2>
            </div>
          </div>

          <div className="sl-card sl-links">
            {!hasLinks && (
              <p className="sl-links-soon">
                {language === 'ru' ? "Скоро на всех площадках" : "Coming soon on all platforms"}
              </p>
            )}
            {language === 'ru' && (
              <>
                {yandex_url && makePlatformLink(yandex_url, yandexIcon, "Яндекс Музыка")}
                {vk_url && makePlatformLink(vk_url, vkIcon, "VK Музыка")}
                {zvuk_url && makePlatformLink(zvuk_url, zvukIcon, "Звук")}
                {mts_url && makePlatformLink(mts_url, mtsIcon, "КИОН Музыка")}
                {appleMusic_url && makePlatformLink(appleMusic_url, appleIcon, "Apple Music")}
                {spotify_url && makePlatformLink(spotify_url, spotifyIcon, "Spotify")}
                {youtubeMusic_url && makePlatformLink(youtubeMusic_url, youtubeIcon, "Youtube Music")}
                {/* После стримингов (пакет Артёма 23.10.2026): Одноклассники — только в русской версии, TikTok — «Звук». */}
                {ok_url && makePlatformLink(ok_url, okIcon, "Одноклассники")}
                {tiktok_url && makePlatformLink(tiktok_url, tiktokIcon, "TikTok", "sound")}
                {beatport_url && makePlatformLink(beatport_url, beatportIcon, "Beatport")}
                {juno_url && makePlatformLink(juno_url, junoIcon, "Juno Download")}
              </>
            )}
            {language === 'en' && (
              <>
                {appleMusic_url && makePlatformLink(appleMusic_url, appleIcon, "Apple Music")}
                {spotify_url && makePlatformLink(spotify_url, spotifyIcon, "Spotify")}
                {youtubeMusic_url && makePlatformLink(youtubeMusic_url, youtubeIcon, "Youtube Music")}
                {deezer_url && makePlatformLink(deezer_url, deezerIcon, "Deezer")}
                {tidal_url && makePlatformLink(tidal_url, tidalIcon, "Tidal")}
                {amazonMusic_url && makePlatformLink(amazonMusic_url, amazonIcon, "Amazon Music")}
                {tiktok_url && makePlatformLink(tiktok_url, tiktokIcon, "TikTok", "sound")}
                {beatport_url && makePlatformLink(beatport_url, beatportIcon, "Beatport")}
                {juno_url && makePlatformLink(juno_url, junoIcon, "Juno Download")}
              </>
            )}
          </div>

          <div className="sl-lang">
            <button
              onClick={() => toggleLanguage('ru')}
              className={`sl-lang-btn ${language === 'ru' ? 'active' : ''}`}
            >
              RU
            </button>
            <button
              onClick={() => toggleLanguage('en')}
              className={`sl-lang-btn ${language === 'en' ? 'active' : ''}`}
            >
              EN
            </button>
          </div>
        </div>
      </div>
    </>
  );

  // kind — подпись кнопки: «Слушать», «Купить» (магазины) или «Звук» (TikTok: использовать трек в видео).
  function makePlatformLink(url, icon, title, kind) {
    const buyPlatforms = ["Beatport", "Juno Download"];
    const isBuy = buyPlatforms.includes(title);
    const btnText = kind === "sound"
      ? (language === 'ru' ? "Звук" : "Sound")
      : isBuy ? (language === 'ru' ? "Купить" : "Buy") : (language === 'ru' ? "Слушать" : "Listen");

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="sl-row-link"
        onClick={handleLinkClick}
      >
        <div className="sl-row">
          <div className="sl-row-left">
            <span className="sl-icon">
              <img alt={`${title} logo`} src={icon} />
            </span>
            <span className="sl-platform-title">{title}</span>
          </div>
          <span className={`sl-btn${isBuy ? " sl-btn--buy" : ""}`}>
            {btnText}
          </span>
        </div>
      </a>
    );
  }
}
